/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import {
  Component,
  effect,
  ElementRef,
  inject,
  input,
  output,
  viewChildren,
} from '@angular/core';
import {
  CalendarClickEvent,
  CalendarDropTask,
  CalendarResourcesDay,
  CalendarTask,
  CommonUtils,
  HourSelectionEvent,
  ResourceDaySelectionEvent,
} from '@infodata-group/inf-commons';
import { CalendarView } from '../calendar-view';
import dayjs from 'dayjs';
import { NgClass, NgStyle, Time } from '@angular/common';
import { Telework } from '../telework';
import { TeleworkService } from '../telework.service';

@Component({
  selector: 'infl-calendar-view-resource',
  templateUrl: './calendar-view-resource.component.html',
  standalone: true,
  styleUrls: ['./calendar-view-resource.component.scss'],
  imports: [NgClass, NgStyle],
})
export class CalendarViewResourceComponent implements CalendarView {
  public resourceCount = input.required<number>();
  public grayedCells = input.required<Date[]>();
  public sections = input.required<CalendarResourcesDay[]>();
  public tasks = input.required<CalendarTask[]>();
  public canDrag = input.required<boolean>();
  public canSelect = input.required<boolean>();
  public enable = input.required<boolean>();
  public dragByOutside = input.required<CalendarDropTask<any>>();
  public dayTimeStart = input.required<Time>();
  public dayTimeEnd = input.required<Time>();
  public teleworks = input.required<Telework[]>();
  public resource = input.required<string>();

  public sectionSelection = output<ResourceDaySelectionEvent>();
  public sectionPreselection = output<ResourceDaySelectionEvent>();
  public clickOnSelection = output<ResourceDaySelectionEvent>();
  public dblClickOnSelection = output<ResourceDaySelectionEvent>();
  public taskClick = output<CalendarClickEvent>();
  public taskDblClick = output<CalendarClickEvent>();
  public taskHover = output<CalendarTask>();
  public dateChange = output<Date>();
  public taskUpdate = output<CalendarTask>();
  public taskCreation = output<Date>();
  public dragTask = output<{
    task: CalendarTask;
    shift: number;
  }>();
  public dropOutside = output<CalendarResourcesDay>();
  public taskContextMenu = output<{
    task: CalendarTask;
    style: any;
  }>();

  protected cells = viewChildren<ElementRef>('cell');

  public selection: boolean;
  public isLoading: boolean;
  public selectedSection: CalendarResourcesDay;
  public viewTasks: { task: CalendarTask; style: any }[][] = [];
  public width: string;
  private draggingTask: { task: CalendarTask; style: any };
  private lastSectionDraggedOver: HTMLDivElement;

  private readonly teleworkService = inject(TeleworkService);

  constructor() {
    effect(() => {
      if (this.sections() || this.tasks()) {
        this.generateTasks();
        this.clearLastSectionHighlighted();
      }
    });
    effect(() => {
      if (this.resourceCount()) {
        this.width = `calc(100% / ${this.resourceCount()})`;
      }
    });
    effect(() => {
      if (this.cells() && this.cells().length > 0 && this.teleworks()) {
        this.loadTelework();
      }
    });
  }

  private loadTelework() {
    for (const section of this.cells()) {
      const div = section.nativeElement;
      const telework = this.teleworks().find((t) => {
        return (
          t.resourceId === this.resource() &&
          t.date.substring(0, 2) === div.getAttribute('data-date')
        );
      });
      if (telework) {
        div.style.backgroundColor = this.teleworkService.teleworkColor;
      }
      div.setAttribute('telework', !!telework);
    }
  }

  public generateTasks() {
    this.draggingTask = null;
    this.viewTasks = [];
    if (this.tasks()) {
      for (const task of this.tasks()) {
        this.generateTask(task);
      }
      for (let i = 0; i < this.viewTasks.length; ++i) {
        if (this.viewTasks[i]) {
          this.sections()[i].setCount(this.viewTasks[i].length);
        }
      }
    }
  }

  public onTaskClick(
    event: MouseEvent,
    task: CalendarTask,
    dblClick?: boolean,
  ) {
    event.preventDefault();
    event.stopPropagation();
    if (dblClick) {
      this.taskDblClick.emit(new CalendarClickEvent(event, task));
    } else {
      this.taskClick.emit(new CalendarClickEvent(event, task));
    }
  }

  public refresh() {
    this.generateTasks();
  }

  onDragStart(task: { task: CalendarTask; style: any }) {
    if (this.canDrag()) {
      this.draggingTask = task;
      this.dragTask.emit({ task: task.task, shift: 0 });
    }
  }

  onDropTask(section: CalendarResourcesDay) {
    if (this.canDrag()) {
      if (this.draggingTask) {
        let start = this.draggingTask.task.dateStart.clone();
        start = start.date(section.date.getDate());
        this.draggingTask.task.updateDateStart(start);
        this.taskUpdate.emit(this.draggingTask.task);
        this.refresh();
      } else if (this.dragByOutside()) {
        const date = section.date;
        this.taskCreation.emit(date);
      } else {
        this.dropOutside.emit(section);
      }
    }
  }

  deselect(): void {
    this.clearSelection();
  }

  public clearLastSectionHighlighted() {
    if (this.lastSectionDraggedOver) {
      this.lastSectionDraggedOver.style.backgroundColor =
        this.lastSectionDraggedOver.getAttribute('telework') === 'true'
          ? this.teleworkService.teleworkColor
          : '';
      this.lastSectionDraggedOver = null;
    }
  }

  public allowDrop(event: DragEvent, cell: HTMLDivElement) {
    event.preventDefault();
    this.clearLastSectionHighlighted();
    this.lastSectionDraggedOver = cell;
    cell.style.backgroundColor = '#FFF2DA';
  }

  private getResourceDaySelectionEvent(
    section: CalendarResourcesDay,
    event: MouseEvent,
  ): ResourceDaySelectionEvent {
    const hourSelection = new HourSelectionEvent(
      { start: this.dayTimeStart(), end: this.dayTimeEnd(), resource: null },
      event,
    );
    return new ResourceDaySelectionEvent(section, hourSelection);
  }

  selectSection(section: CalendarResourcesDay, event: MouseEvent) {
    if (this.canSelect()) {
      if (this.selectedSection !== section) {
        this.selectedSection = section;
        this.sectionSelection.emit(
          this.getResourceDaySelectionEvent(section, event),
        );
      } else {
        this.clickOnSelection.emit(
          this.getResourceDaySelectionEvent(section, event),
        );
      }
    }
  }

  selectByDblClick(section: CalendarResourcesDay, event: MouseEvent) {
    this.dblClickOnSelection.emit(
      this.getResourceDaySelectionEvent(section, event),
    );
  }

  onOverZone(section: CalendarResourcesDay, event: MouseEvent) {
    this.sectionPreselection.emit(
      this.getResourceDaySelectionEvent(section, event),
    );
  }

  findTask(id: string): { style: any; task: CalendarTask } {
    for (const v of this.viewTasks) {
      const t = v.find((vt) => vt.task.id === id);
      if (t) {
        return t;
      }
    }
    return null;
  }

  getScrollPosition(): { x: number; y: number } {
    return null;
  }

  setScrollPosition(x: number, y: number) {
    // DO Nothing
  }

  private generateTask(task: CalendarTask) {
    const dayCount = task.dayDuration();
    let date = task.dateStart;
    const style = {
      color: task.color,
      borderColor: task.backgroundColor,
      top: 'O',
    };
    for (let i = 0; i < dayCount; ++i) {
      const index = this.sections().findIndex((s) =>
        CommonUtils.compareDate(date.toDate(), s.date),
      );
      date = dayjs(date).add(1, 'd');
      if (index >= 0) {
        if (!this.viewTasks[index]) {
          this.viewTasks[index] = [];
        }
        style.top = `${this.viewTasks[index].length * 27}px`;
        this.viewTasks[index].push({
          task: task,
          style: style,
        });
      }
    }
  }

  private clearSelection() {
    this.selectedSection = null;
  }

  public scrollToDate(date: Date): void {}

  public setTelework(cell: HTMLDivElement) {
    cell.style.backgroundColor = this.teleworkService.teleworkColor;
    cell.setAttribute('telework', 'ok');
  }
}
